import { z } from "zod/v4";

import {
  AnalyticsChannelList,
  AnalyticsSelectedProductSchema,
  AnalyticsViewTypeFeList,
  AnalyzeExperienceFrontendRequestBodySchema,
  CastedTimes,
  castToBool,
  castToString,
  ConversionStageReachedList,
} from "@intelligems/ig-types";
import type { DashboardPageName } from "@src/api/types/dashboards";

export const IExperienceDashboardFiltersSchema =
  AnalyzeExperienceFrontendRequestBodySchema.pick({
    customerType: true,
    deviceType: true,
    visitorType: true,
    userBehavior: true,
    urlParam: true,
    landingPage: true,
    landingPageFilters: true,
    landingPageFiltersOperator: true,
    landingPageTypes: true,
    countryCodes: true,
    countryCodesFilterType: true,
    maxZScore: true,
    shippingExpensePerOrder: true,
    transactionFeePct: true,
    return_variants: true,
    customEventsFilterType: true,
    shippingMethods: true,
    topN: true,
    showExperimentProductsOnly: true,
  }).extend({
    experienceId: z.string().optional(),
    compareGroupA: z.string().optional(),
    compareGroupB: z.string().optional(),
    isZScoreEnabled: z.boolean(),
    pageName: z.string(),
    hiddenEndTime: z.number().nullable().optional(),
    reachedCheckoutStage: z.enum(ConversionStageReachedList).optional(),
    minNetRevenue: z.number().nullable(),
    maxNetRevenue: z.number().nullable(),
    minShippingRevenue: z.number().nullable(),
    maxShippingRevenue: z.number().nullable(),
    customEvents: z.string().array().default([]),
    // Analytics filter, so AnalyticsChannelList — not the audience-targeting
    // ChannelTypeSchema the plugin evaluates client-side.
    channels: z.enum(AnalyticsChannelList).array(),
    startTime: castToString.or(z.number()).nullable(),
    endTime: castToString.or(z.number()).nullable(),
    analyticsViewType: z.enum(AnalyticsViewTypeFeList),
    selectedAnalyticsProducts: AnalyticsSelectedProductSchema.array().default(
      []
    ),
    excludeFreeOrders: z.boolean(),
    includeTestOrders: z.boolean().optional(),
  });

export type IExperienceDashboardFilters = z.infer<
  typeof IExperienceDashboardFiltersSchema
> &
  CastedTimes;

export const initialDashboardFiltersState: IExperienceDashboardFilters = {
  experienceId: undefined,
  startTime: null,
  endTime: null,
  analyticsViewType: "sitewide_orders",
  compareGroupA: "",
  compareGroupB: "",
  isZScoreEnabled: true,
  maxZScore: null,
  customEvents: [],
  countryCodes: [],
  countryCodesFilterType: "include",
  customerType: null,
  deviceType: "any",
  visitorType: "any",
  shippingExpensePerOrder: null,
  transactionFeePct: null,
  channels: [],
  customEventsFilterType: "any",
  urlParam: {
    queryParam: null,
    queryParamValue: null,
    queryParamFilterType: null,
  },
  landingPage: {
    landingPageUrlPath: null,
    landingPageUrlPathFilterType: null,
  },
  landingPageFilters: [],
  landingPageFiltersOperator: "or" as const,
  landingPageTypes: undefined,
  userBehavior: {
    viewedCheckout: null,
    addedToCart: null,
    viewedCollectionPage: null,
    viewedProductPage: null,
  },
  pageName: (localStorage.getItem("DashboardFilters.pageName") ??
    "overview") as DashboardPageName,
  hiddenEndTime: null,
  reachedCheckoutStage: "any",
  shippingMethods: [],
  minNetRevenue: null,
  maxNetRevenue: null,
  minShippingRevenue: null,
  maxShippingRevenue: null,
  selectedAnalyticsProducts: [],
  excludeFreeOrders: true,
  topN: 10, // top n products rn
  showExperimentProductsOnly: null,
  includeTestOrders: true,
};

export type AnalyticsActiveFilterType = {
  // key: keyof IExperienceDashboardFilters | keyof SitewideDashboardFiltersDialog;
  key: string;
  label: any;
  value: any;
};
